import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../context/CookieConsentContext';

const CookieSettingsModal = ({ analytics, advertising, setAnalytics, setAdvertising, onSave, onReject, onAcceptAll, onClose }) => {
  const { t } = useTranslation('global');

  const handleSave = () => {
    if (!analytics && !advertising) {
      onReject();
      return;
    }
    onSave({ analytics, advertising });
  };

  return (
    <div className="cookie-overlay" role="presentation" onClick={onClose}>
      <div
        className="cookie-modal"
        role="dialog"
        aria-labelledby="cookie-modal-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="cookie-modal-title">{t('cookies.configureTitle')}</h2>
        <p>{t('cookies.configureDesc')}</p>

        <div className="cookie-modal__options">
          <label className="cookie-option cookie-option--locked">
            <input type="checkbox" checked disabled readOnly />
            <span>
              <strong>{t('cookies.necessaryTitle')}</strong>
              <small>{t('cookies.necessaryDesc')}</small>
            </span>
          </label>
          <label className="cookie-option">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
            />
            <span>
              <strong>{t('cookies.analyticsTitle')}</strong>
              <small>{t('cookies.analyticsDesc')}</small>
            </span>
          </label>
          <label className="cookie-option">
            <input
              type="checkbox"
              checked={advertising}
              onChange={(e) => setAdvertising(e.target.checked)}
            />
            <span>
              <strong>{t('cookies.advertisingTitle')}</strong>
              <small>{t('cookies.advertisingDesc')}</small>
            </span>
          </label>
        </div>

        <div className="cookie-modal__buttons">
          <button type="button" className="cookie-btn cookie-btn--ghost" onClick={onReject}>
            {t('cookies.rejectAll')}
          </button>
          <button type="button" className="cookie-btn cookie-btn--secondary" onClick={handleSave}>
            {t('cookies.savePreferences')}
          </button>
          <button type="button" className="cookie-btn cookie-btn--primary" onClick={onAcceptAll}>
            {t('cookies.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

const CookieBanner = () => {
  const { t } = useTranslation('global');
  const {
    consent,
    pendingConsent,
    settingsOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const [analytics, setAnalytics] = useState(consent.analytics);
  const [advertising, setAdvertising] = useState(consent.advertising);

  useEffect(() => {
    if (settingsOpen) {
      setAnalytics(consent.analytics);
      setAdvertising(consent.advertising);
    }
  }, [settingsOpen, consent.analytics, consent.advertising]);

  const showBar = pendingConsent && !settingsOpen;
  const showModal = settingsOpen;

  if (!showBar && !showModal) return null;

  return (
    <>
      {showBar && (
        <aside className="cookie-bar" role="dialog" aria-labelledby="cookie-bar-title" aria-live="polite">
          <div className="cookie-bar__inner">
            <p className="cookie-bar__text" id="cookie-bar-title">
              <strong>{t('cookies.barTitle')}</strong>{' '}
              {t('cookies.barText')}{' '}
              <Link to="/politica-cookies">{t('cookies.policyLink')}</Link>.
            </p>
            <div className="cookie-bar__actions">
              <button type="button" className="cookie-btn cookie-btn--ghost" onClick={rejectAll}>
                {t('cookies.rejectAll')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--secondary" onClick={openSettings}>
                {t('cookies.configure')}
              </button>
              <button type="button" className="cookie-btn cookie-btn--primary" onClick={acceptAll}>
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>
        </aside>
      )}

      {showModal && (
        <CookieSettingsModal
          analytics={analytics}
          advertising={advertising}
          setAnalytics={setAnalytics}
          setAdvertising={setAdvertising}
          onSave={savePreferences}
          onReject={rejectAll}
          onAcceptAll={acceptAll}
          onClose={pendingConsent ? () => {} : closeSettings}
        />
      )}
    </>
  );
};

export default CookieBanner;

